import axios from 'axios';
import { createBackendRecovery, shouldRecoverBackend } from './apiRecovery';

const resolveBaseUrl = () => {
  const explicit = String(process.env.REACT_APP_API_BASE_URL || '').trim();
  if (explicit) return explicit;
  if (typeof window !== 'undefined') {
    const hostname = String(window.location.hostname || '').trim().toLowerCase();
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '';
    }
  }
  return 'https://note-taker-3-unrg.onrender.com';
};

const BASE_URL = resolveBaseUrl();
let authRedirectInFlight = false;
const AUTH_ROUTE_PATTERN = /\/api\/auth\/(login|register)$/i;

const isPublicAuthRequest = (config = {}) => {
  if (config?.skipAuthHandling) return true;
  return AUTH_ROUTE_PATTERN.test(String(config?.url || ''));
};

const parseJwtExpiry = (token) => {
  try {
    const payloadPart = String(token || '').split('.')[1];
    if (!payloadPart) return 0;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded));
    return Number(decoded?.exp || 0);
  } catch (_error) {
    return 0;
  }
};

const clearStoredTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('jwt');
  if (window.chrome && window.chrome.storage) {
    window.chrome.storage.local.remove(['token', 'authToken', 'jwt']);
  }
  // The nightstand kept for offline reading, and the highlights and places
  // waiting to be sent from it (public/sw.js), are this account's.
  window.caches?.delete('noeis-reading');
};

const redirectToLogin = (reason = 'auth') => {
  // Work signs in on its own page, so writing that has not reached the server stays put.
  if (authRedirectInFlight || window.location.pathname === '/work') return;
  authRedirectInFlight = true;
  clearStoredTokens();
  try {
    sessionStorage.setItem('auth_redirect_reason', reason);
    const currentPath = `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`;
    if (currentPath && currentPath !== '/login') sessionStorage.setItem('auth_return_to', currentPath);
  } catch (_error) {
    // ignore storage failures
  }
  window.location.href = '/login';
};

export const hasUsableStoredToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  const parts = String(token).split('.');
  if (parts.length !== 3) return false;
  const exp = parseJwtExpiry(token);
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return exp > now;
};

// Create a new instance of axios
const api = axios.create({
  baseURL: BASE_URL,
});

const recoverBackend = createBackendRecovery({
  probe: () => axios.get(`${BASE_URL}/health`, {
    timeout: 5000,
    headers: { Accept: 'application/json' }
  })
});

api.interceptors.request.use((config) => {
  if (isPublicAuthRequest(config)) return config;
  const token = localStorage.getItem('token');
  if (!token) return config;

  const exp = parseJwtExpiry(token);
  const now = Math.floor(Date.now() / 1000);
  if (exp > 0 && exp <= now) {
    redirectToLogin('expired');
    const error = new Error('AUTH_EXPIRED');
    error.code = 'AUTH_EXPIRED_LOCAL';
    return Promise.reject(error);
  }

  if (!config.headers) config.headers = {};
  if (!config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add a response interceptor
api.interceptors.response.use(
  // If the response is successful, just return it
  (response) => response,
  // If the response has an error...
  async (error) => {
    if (isPublicAuthRequest(error.config)) {
      return Promise.reject(error);
    }
    // Check if the error is a 401 (Unauthorized) or 403 (Forbidden)
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const authError = error.response.data?.error;
      const reason = authError === 'AUTH_EXPIRED' ? 'expired' : 'unauthorized';
      console.log("Authentication error detected. Redirecting to login.");
      redirectToLogin(reason);
    }
    if (shouldRecoverBackend(error)) {
      try {
        await recoverBackend();
        return api.request({
          ...error.config,
          __noeisWakeRetry: true
        });
      } catch (_recoveryError) {
        // Preserve the original request error so the surface can report it honestly.
      }
    }
    // For all other errors, just pass them along
    return Promise.reject(error);
  }
);

export default api;
export { clearStoredTokens };
